import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Form, Input, Row, Col, Button, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  GET_ADDRESS_RESIDENTIAL_AREA_DETAIL,
  GetAddressResidentialAreaDetailResponse,
  AddressResidentialAreaImageType,
} from '../graphql/queries/getAddressResidentialAreaDetail';
import {
  UPDATE_ADDRESS_RESIDENTIAL_AREA,
} from '../graphql/mutations/upsertAddressResidentialArea';
import ImageUploader from "./ImageUploader";
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

type DrawerMode = 'view' | 'edit';

interface Props {
  visible: boolean;
  onClose: () => void;
  areaId: string | null;
  /** 查看详情时不显示保存按钮，编辑时显示 */
  mode?: DrawerMode;
  /** 查看详情模式下点击编辑时回调，由父组件切换为编辑模式 */
  onSwitchToEdit?: () => void;
  /** 查看模式下最多展示的图片数，默认 5 */
  maxDisplayImages?: number;
  /** 图片区块标题，默认使用 drawer.sectionImagesTitle */
  imagesSectionTitle?: string;
  /** 审核图片（来自审核窗口），仅编辑模式下展示，支持拖拽到小区地图图片 */
  reportedImages?: Array<{ imageS3Key: string; imageDisplayUrl?: string | null }>;
  /** 上报数据ID（来自审核窗口），保存时传入接口 */
  reportDataId?: string;
}

const PREFIX = 'mapTool.addressResidentialArea.drawer';

const AddressResidentialAreaDrawer: React.FC<Props> = ({
  visible,
  onClose,
  areaId,
  mode = 'edit',
  onSwitchToEdit,
  maxDisplayImages = 5,
  imagesSectionTitle,
  reportedImages,
  reportDataId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { data, loading, refetch } = useQuery<GetAddressResidentialAreaDetailResponse>(
    GET_ADDRESS_RESIDENTIAL_AREA_DETAIL,
    {
      skip: !areaId,
      variables: { id: areaId ? Number(areaId) : undefined },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [updateArea, { loading: updatingArea }] = useMutation(UPDATE_ADDRESS_RESIDENTIAL_AREA);

  useEffect(() => {
    if (data?.addressResidentialAreaDetail) {
      form.setFieldsValue({
        residentialAddress: data.addressResidentialAreaDetail.residentialAddress,
        standardAddress: data.addressResidentialAreaDetail.standardAddress,
        residentialName: data.addressResidentialAreaDetail.residentialName,
        stateAbbreviation: data.addressResidentialAreaDetail.stateAbbreviation,
        cityName: data.addressResidentialAreaDetail.cityName,
        zipcode: data.addressResidentialAreaDetail.zipcode,
      });
    } else {
      form.resetFields();
    }
  }, [data, form, visible]);

  const MAX_IMAGES = 5;
  const images: AddressResidentialAreaImageType[] | undefined =
    data?.addressResidentialAreaDetail?.images ?? undefined;

  // 按 stopNumber 填充 5 个固定图片位，支持前端直接调整顺序
  const [slots, setSlots] = useState<(AddressResidentialAreaImageType | null)[]>(() =>
    Array(MAX_IMAGES).fill(null)
  );

  // 编辑模式下用于保存时计算图片 diff 的初始快照（仅从服务端加载时更新）
  const initialSlotsRef = useRef<(AddressResidentialAreaImageType | null)[]>(Array(MAX_IMAGES).fill(null));

  // 编辑模式下各图片位是否正在上传，任一上传中则禁止保存
  const [uploadingSlots, setUploadingSlots] = useState<Record<number, boolean>>({});
  const hasAnyUploading = Object.values(uploadingSlots).some(Boolean);

  // 上报图片（只读，仅用于拖拽到小区地图图片）
  const reportedSlots = reportedImages
    ? reportedImages.slice(0, MAX_IMAGES)
    : [];

  // 拖拽状态：正在拖拽的上报图片索引 / 当前悬停的主图片槽索引
  const [draggingReportedIndex, setDraggingReportedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // 当后端数据变化时，按 stopNumber 重新映射到本地 slots，并同步初始快照
  // 当 images 为 undefined（查询被 skip 或正在加载）时不重置 slots，
  // 避免关闭 Drawer 后 slots 被清空，导致重新打开同一条数据时因 images 引用不变而 useEffect 不触发
  useEffect(() => {
    if (images === undefined) return;
    const next: (AddressResidentialAreaImageType | null)[] = Array(MAX_IMAGES).fill(null);
    if (images.length > 0) {
      const sorted = [...images].sort((a, b) => a.stopNumber - b.stopNumber);
      sorted.forEach((img) => {
        const idx = img.stopNumber - 1;
        if (idx >= 0 && idx < MAX_IMAGES) {
          next[idx] = img;
        }
      });
    }
    setSlots(next);
    initialSlotsRef.current = next.map((s) => (s ? { ...s } : null));
  }, [images]);

  // 编辑模式下仅更新本地 slots，不调接口；保存时统一提交
  const handleSlotUploadChange = (
    slotIndex: number,
    urls: string[],
    previewUrls?: string[],
  ) => {
    if (!areaId) {
      return;
    }
    const current = slots[slotIndex];

    if (urls.length === 0) {
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = null;
        return next;
      });
      return;
    }

    const objectKey = urls[0];
    const displayUrl = previewUrls?.[0] ?? null;
    if (current) {
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = { ...current, imageS3Key: objectKey, imageDisplayUrl: displayUrl ?? current.imageDisplayUrl };
        return next;
      });
    } else {
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = {
          id: undefined as unknown as string,
          residentialAreaId: Number(areaId),
          imageS3Key: objectKey,
          stopNumber: slotIndex + 1,
          imageDisplayUrl: displayUrl,
        } as AddressResidentialAreaImageType;
        return next;
      });
    }
  };

  // 编辑模式下仅本地交换位置，不调接口；保存时统一提交（与 Add 一致：直接交换数组两项）
  const handleMove = (slotIndex: number, direction: 'left' | 'right') => {
    const targetIndex = direction === 'left' ? slotIndex - 1 : slotIndex + 1;
    if (targetIndex < 0 || targetIndex >= slots.length) return;

    const current = slots[slotIndex];
    const target = slots[targetIndex];
    if (!current || !target) return;

    setSlots((prev) => {
      const next = [...prev];
      const tmp = next[slotIndex];
      next[slotIndex] = next[targetIndex];
      next[targetIndex] = tmp;
      return next;
    });
  };

  const handleSaveArea = async () => {
    if (!areaId) {
      return;
    }
    if (hasAnyUploading) {
      message.warning(t(`${PREFIX}.messageWaitForUploadFinish`));
      return;
    }
    try {
      const values = await form.validateFields();
      const images: { id?: number; imageS3Key: string; stopNumber: number }[] = slots
        .map((slot, index) =>
          slot?.imageS3Key
            ? {
                id: slot.id ? Number(slot.id) : undefined,
                imageS3Key: slot.imageS3Key,
                stopNumber: index + 1,
              }
            : null
        )
        .filter((item): item is NonNullable<typeof item> => item != null);

      if (mode === 'edit' && images.length === 0) {
        message.warning(t(`${PREFIX}.messageMinOneImage`));
        return;
      }

      await updateArea({
        variables: {
          id: Number(areaId),
          input: {
            residentialAddress: values.residentialAddress,
            standardAddress: values.standardAddress ?? data?.addressResidentialAreaDetail?.standardAddress ?? '',
            residentialName: values.residentialName,
            stateAbbreviation: values.stateAbbreviation || null,
            cityName: values.cityName || null,
            zipcode: values.zipcode || null,
            images,
            reportDataId: reportDataId ? Number(reportDataId) : null,
          },
        },
      });

      message.success(t(`${PREFIX}.messageSaveSuccess`));
      await refetch();
      onClose();
    } catch (e: any) {
      if (e?.name === 'ValidationError') {
        return;
      }
      message.error(e?.message || t(`${PREFIX}.messageSaveFailed`));
    }
  };

  return (
    <Drawer
      title={t(`${PREFIX}.title`)}
      width={1000}
      open={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t(`${PREFIX}.buttonClose`)}</Button>
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.UPDATE]}>
            {mode === 'view' && onSwitchToEdit && (
              <Button type="primary" onClick={onSwitchToEdit}>
                {t(`${PREFIX}.buttonEdit`)}
              </Button>
            )}
            {mode === 'edit' && (
              <Button
                type="primary"
                onClick={handleSaveArea}
                loading={updatingArea}
                disabled={!areaId}
              >
                {t(`${PREFIX}.buttonSave`)}
              </Button>
            )}
          </HasResource>
        </div>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 20 }}
        className="mb-6"
      >
        {/* 第一行：ID */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t(`${PREFIX}.labelId`) as string}>
              <Input value={data?.addressResidentialAreaDetail?.id ?? ''} disabled readOnly />
            </Form.Item>
          </Col>
        </Row>

        {/* 第二行：小区名称 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="residentialName"
              label={t(`${PREFIX}.labelResidentialName`)}
              rules={[{ max: 200, message: t('common.max200Characters') as string }]}
            >
              <Input
                placeholder={t(`${PREFIX}.placeholderResidentialName`) as string}
                disabled={mode === 'view'}
                maxLength={200}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第三行：创建时间 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t(`${PREFIX}.labelCreatedAt`) as string}>
              <Input
                value={
                  data?.addressResidentialAreaDetail?.createdAt
                    ? new Date(data.addressResidentialAreaDetail.createdAt).toLocaleString()
                    : ''
                }
                disabled
                readOnly
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第四行：最后更新时间 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t(`${PREFIX}.labelUpdatedAt`) as string}>
              <Input
                value={
                  data?.addressResidentialAreaDetail?.updatedAt
                    ? new Date(data.addressResidentialAreaDetail.updatedAt).toLocaleString()
                    : ''
                }
                disabled
                readOnly
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第五行：原始地址 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="residentialAddress" label={t(`${PREFIX}.labelOriginalAddress`) as string}>
              <Input placeholder={t(`${PREFIX}.placeholderResidentialAddress`) as string} disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* 第六行：标准化地址 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="standardAddress"
              label={t(`${PREFIX}.labelStandardAddress`) as string}
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* 第七行：州 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="stateAbbreviation" label={t(`${PREFIX}.labelState`)}>
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* 第八行：城市 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="cityName" label={t(`${PREFIX}.labelCity`)}>
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* 第九行：邮编 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="zipcode" label={t(`${PREFIX}.labelZipcode`)}>
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* 第十行：图片 */}
      <div className="mb-2 mt-4">
        <h4 className="text-lg font-semibold">{imagesSectionTitle ?? t(`${PREFIX}.sectionImagesTitle`)}</h4>
      </div>

      {/* 始终显示 5 个图片位，与 Add 一致：每格固定一个 ImageUploader，按位置 key=index，移动时只交换数据 */}
      <div className="grid grid-cols-5 gap-4">
        {slots.slice(0, mode === 'view' ? maxDisplayImages : MAX_IMAGES).map((slot, index) => {
          const hasImage = !!slot?.imageS3Key;
          const leftDisabled = !hasImage || index === 0 || !slots[index - 1]?.imageS3Key;
          const rightDisabled = !hasImage || index === MAX_IMAGES - 1 || !slots[index + 1]?.imageS3Key;
          const showPlaceholder = mode === 'view' && !hasImage;
          const isDropTarget = draggingReportedIndex !== null && dropTargetIndex === index;
          return (
          <div
            key={index}
            className={`flex flex-col items-center min-h-[104px] ${!hasImage ? 'opacity-70' : ''} ${isDropTarget ? 'ring-2 ring-blue-400 rounded-lg' : ''}`}
            onDragOver={(e) => { if (draggingReportedIndex !== null) { e.preventDefault(); setDropTargetIndex(index); } }}
            onDragLeave={() => setDropTargetIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggingReportedIndex === null) return;
              const src = reportedSlots[draggingReportedIndex];
              if (!src?.imageS3Key) return;
              setSlots((prev) => {
                const next = [...prev];
                next[index] = {
                  id: undefined as unknown as string,
                  residentialAreaId: Number(areaId),
                  imageS3Key: src.imageS3Key,
                  stopNumber: index + 1,
                  imageDisplayUrl: src.imageDisplayUrl ?? null,
                } as AddressResidentialAreaImageType;
                return next;
              });
              setDraggingReportedIndex(null);
              setDropTargetIndex(null);
            }}
          >
            {showPlaceholder ? (
            <div className="w-full min-h-[104px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
              {t(`${PREFIX}.noImage`)}
            </div>
            ) : (
            <ImageUploader
              value={slot?.imageS3Key ? [slot.imageS3Key] : []}
              displayUrls={slot?.imageDisplayUrl ? [slot.imageDisplayUrl] : []}
              onChange={(urls, previewUrls) => handleSlotUploadChange(index, urls, previewUrls)}
              onUploadingChange={mode === 'edit' ? (uploading) => setUploadingSlots((prev) => ({ ...prev, [index]: uploading })) : undefined}
              maxCount={1}
              maxSizeMB={25}
              tag="address-residential-area"
              disabled={mode === 'view'}
              usePublicBucket
            />
            )}
            {mode === 'edit' && hasImage && (
              <div className="mt-2 flex gap-2">
                <Button
                  type="default"
                  size="small"
                  icon={<LeftOutlined style={{ color: leftDisabled ? '#d9d9d9' : undefined }} />}
                  onClick={() => handleMove(index, 'left')}
                  style={leftDisabled ? { borderColor: '#d9d9d9', cursor: 'not-allowed' } : undefined}
                />
                <Button
                  type="default"
                  size="small"
                  icon={<RightOutlined style={{ color: rightDisabled ? '#d9d9d9' : undefined }} />}
                  onClick={() => handleMove(index, 'right')}
                  style={rightDisabled ? { borderColor: '#d9d9d9', cursor: 'not-allowed' } : undefined}
                />
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* 上报图片区块：仅编辑模式且存在上报图片时显示，图片可拖拽到上方小区地图图片中 */}
      {mode === 'edit' && reportedImages !== undefined && (
        <>
          <div className="mb-2 mt-6">
            <h4 className="text-lg font-semibold">{t(`${PREFIX}.sectionReportedImagesTitle`)}</h4>
          </div>
          <div className="grid grid-cols-5 gap-4">
            {reportedSlots.map((slot, index) => (
              <div
                key={index}
                draggable={!!slot?.imageS3Key}
                onDragStart={() => { if (slot?.imageS3Key) setDraggingReportedIndex(index); }}
                onDragEnd={() => { setDraggingReportedIndex(null); setDropTargetIndex(null); }}
                className={`flex flex-col items-center min-h-[104px] ${draggingReportedIndex === index ? 'opacity-50' : ''}`}
                style={{ cursor: slot?.imageS3Key ? 'grab' : 'default' }}
              >
                {slot?.imageS3Key ? (
                  <ImageUploader
                    value={[slot.imageS3Key]}
                    displayUrls={slot.imageDisplayUrl ? [slot.imageDisplayUrl] : []}
                    onChange={() => {}}
                    maxCount={1}
                    maxSizeMB={25}
                    tag="address-residential-area"
                    disabled
                    usePublicBucket
                  />
                ) : (
                  <div className="w-full min-h-[104px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                    {t(`${PREFIX}.noImage`)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default AddressResidentialAreaDrawer;

